import type { CompetitionParticipant } from '../../types/competition';
import type { RoundResult } from '../../types/competition-round';

/**
 * The result a participant scored on a problem, out of the problem's results.
 *
 * Matched on the entry itself first: a result is recorded for a
 * CompetitionParticipant, and that id is the one thing both rows agree on. The
 * account is no good as a key — somebody entered by email climbs under a
 * placeholder account the entry list does not report (`user_id` is null there)
 * while their result does carry it, so matching on `user_id` finds nothing and
 * a row would stay blank however many attempts the judge records. The account
 * and the address are kept as a fallback for results older than the entry list.
 */
export function findResultForParticipant<R extends RoundResult>(
  participant: CompetitionParticipant,
  results: R[]
): R | undefined {
  const own = results.find(r => r.competition_participant_id === participant.id);
  if (own) return own;

  return results.find(r =>
    !r.competition_participant_id && (
      (participant.user_id && r.user_id === participant.user_id) ||
      r.user?.name === participant.email
    )
  );
}

export function getParticipantDisplayText(participant: CompetitionParticipant): string {
  const displayName = participant.full_name || participant.user?.full_name || participant.email;
  const category = participant.category?.name;
  const team = participant.team?.name;

  const parts: string[] = [displayName];
  if (category) {
    parts.push(`(${category})`);
  }
  if (team) {
    parts.push(`- ${team}`);
  }

  return parts.join(' ');
}

/**
 * The Cognome column. An entry made from an email address alone has no name on
 * it yet, so the column is simply empty.
 */
export function getParticipantSurname(participant: CompetitionParticipant): string {
  return participant.surname || participant.user?.surname || '';
}

/**
 * The Nome column. When nothing is split into name and surname the whole name
 * — or, failing that, the address the person was invited with — stands in it,
 * so a row is never blank.
 */
export function getParticipantName(participant: CompetitionParticipant): string {
  const name = participant.name || participant.user?.name;
  if (name) return name;
  if (getParticipantSurname(participant)) return '';

  return participant.full_name || participant.user?.full_name || participant.email;
}

export function getParticipantTeam(participant: CompetitionParticipant): string {
  return participant.team?.name || '';
}
